import { BookFillIcon } from '@applyboard/ui-icons';
import { useAppProcessing, useAdditionalDocumentRequest } from '~/pages/ApplicationDetails/context';
import { Flex, Text, GridAlpha } from '@applyboard/crystal-ui';
import { SingleDocumentRequest } from '~/pages/ApplicationDetails/components/ApplicationProcessing/AdditionalDocumentRequest/SingleDocumentRequest';
import { TestType, HasLanguageProficiencyCodes } from '@ab/ams-school-api/types/private/v1';
import { LabelValue } from '~/components/uie-fix/LabelValue';
import { getDocumentReviewStatus, isVerifyExamEnabled } from '~/utils/languageProficiency';
import { useGetCustomizedDocumentTypes } from '~/pages/ApplicationDetails/data/useGetCustomizedDocumentTypes';
import { LanguageProficiencyToast } from './LanguageProficiency/LanguageProficiencyToast';
import { LanguageProficiencyData } from './LanguageProficiency/LanguageProficiencyData';
import { Card } from './Card';
import { AttachedDocuments } from './shared/AttachedDocuments';

export function LanguageProficiency() {
  const {
    application: { languageProficiency },
  } = useAppProcessing();
  const additionalDocumentRequest = useAdditionalDocumentRequest();
  const { customizedDocumentTypes } = useGetCustomizedDocumentTypes();

  const isVerifyExamEnabledResult =
    languageProficiency.status === HasLanguageProficiencyCodes.HAVE &&
    isVerifyExamEnabled(
      customizedDocumentTypes,
      languageProficiency.languageProficiencyData.testType.code as TestType as TestType | undefined
    );

  const isOtherTestTypes =
    languageProficiency.status === HasLanguageProficiencyCodes.HAVE &&
    [
      TestType.OTHER,
      TestType.CAEL,
      TestType.CAMBRIDGE,
      TestType.MET,
      TestType.EPP,
      TestType.CANTEST,
      TestType.LANGUAGECERT_ESOL,
      TestType.LEAP_4_ADVANCED,
    ].includes(languageProficiency.languageProficiencyData.testType.code as TestType);

  const isAIEnabledResult =
    isVerifyExamEnabledResult && !languageProficiency.languageProficiencyData.isAttachedToApplication;

  return (
    <Card
      id="languageProficiency"
      icon={BookFillIcon}
      title="Language Proficiency"
      tag={isAIEnabledResult ? { text: 'This section is AI enabled', intent: 'primary' } : undefined}
    >
      {languageProficiency.status === HasLanguageProficiencyCodes.DONT_NEED && (
        <Flex gap={4} direction="column">
          <Text variant="bodyM" contrast="mid">
            N/A
          </Text>
          {additionalDocumentRequest.isEnabled && (
            <SingleDocumentRequest
              documentTypeCode="LANGUAGE_TEST"
              documentTypeName="Language Proficiency Proof"
              mode="ADD"
              sectionReference="LANGUAGE_TEST"
            />
          )}
        </Flex>
      )}
      {languageProficiency.status === HasLanguageProficiencyCodes.WILL_HAVE && (
        <Flex gap={4} direction="column">
          <Text variant="bodyM" contrast="mid">
            To be provided.
          </Text>
          {additionalDocumentRequest.isEnabled && (
            <SingleDocumentRequest
              documentTypeCode="LANGUAGE_TEST"
              documentTypeName="Language Proficiency Proof"
              mode="ADD"
              sectionReference="LANGUAGE_TEST"
            />
          )}
        </Flex>
      )}

      {languageProficiency.status === HasLanguageProficiencyCodes.HAVE && (
        <LanguageProficiencyData
          isVerifyExamEnabledResult={isVerifyExamEnabledResult}
          isOtherTestTypes={isOtherTestTypes}
          languageProficiency={languageProficiency}
          documentReviewStatus={getDocumentReviewStatus(languageProficiency.languageProficiencyData)}
        />
      )}
      <Flex gap={10} direction="column">
        {languageProficiency.status === HasLanguageProficiencyCodes.HAVE && (
          <GridAlpha rows="repeat(1, auto)" columnGap={2} rowGap={6} columns="repeat(5, 1fr)">
            <GridAlpha.Item rowStart={2} rowEnd={2} columnStart={1} columnEnd={5}>
              <Flex direction="column" gap={4}>
                <LabelValue label="" value="Attached file" variant="labelL" />
                <AttachedDocuments
                  attachmentDocuments={languageProficiency.languageProficiencyData.documents}
                  section="languageProficiency"
                  sectionReference={languageProficiency.languageProficiencyData.key}
                  noRequestForMissingDocuments
                />
              </Flex>
            </GridAlpha.Item>
          </GridAlpha>
        )}

        {languageProficiency.status === HasLanguageProficiencyCodes.HAVE && !isOtherTestTypes && (
          <LanguageProficiencyToast
            documentReviewStatus={getDocumentReviewStatus(languageProficiency.languageProficiencyData)}
          />
        )}
      </Flex>
    </Card>
  );
}
